import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { ShieldAlert, Loader2, Check, RefreshCw, MessageSquareWarning } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Flagged Comments Queue — captions & comments the Christian-only text filter
 * blocked, so a moderator can review borderline calls.
 */
const ContentModerationPanel = ({ language = 'en' }) => {
  const es = language === 'es';
  const [items, setItems] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('open'); // open | resolved | all
  const [actionId, setActionId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/moderation/content-log`, {
        params: { status },
        withCredentials: true,
      });
      setItems(r.data?.items || []);
      setOpenCount(r.data?.open_count || 0);
    } catch (e) {
      toast.error(es ? 'Error al cargar' : 'Failed to load moderation log');
    } finally {
      setLoading(false);
    }
  }, [status, es]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    setActionId(id);
    try {
      await axios.post(`${BACKEND_URL}/api/moderation/content-log/${id}/resolve`, {}, { withCredentials: true });
      setItems((p) => p.filter((i) => i.id !== id));
      setOpenCount((c) => Math.max(0, c - 1));
      toast.success(es ? 'Marcado como revisado' : 'Marked reviewed');
    } catch (e) {
      toast.error(es ? 'Error' : 'Failed');
    } finally {
      setActionId(null);
    }
  };

  return (
    <Card data-testid="content-moderation-panel">
      <CardHeader className="pb-3">
        <div className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquareWarning className="w-4 h-4 text-primary" />
            {es ? 'Comentarios bloqueados' : 'Flagged Comments Queue'}
            {openCount > 0 && (
              <Badge variant="outline" className="bg-red-500/15 text-red-500 border-red-500/30 text-[10px]">
                {openCount} {es ? 'abiertos' : 'open'}
              </Badge>
            )}
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="content-mod-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {es
            ? 'Textos que el filtro cristiano bloqueó al publicar. Revisa los casos límite.'
            : 'Captions & comments the Christian-only filter blocked. Review borderline calls.'}
        </p>
        <div className="flex gap-1.5 mt-2">
          {[
            ['open', es ? 'Abiertos' : 'Open'],
            ['resolved', es ? 'Resueltos' : 'Resolved'],
            ['all', es ? 'Todos' : 'All'],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setStatus(key)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${status === key ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/40 hover:bg-muted border-border'}`}
              data-testid={`content-mod-filter-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading && (
          <div className="py-10 text-center text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />{es ? 'Cargando…' : 'Loading…'}
          </div>
        )}
        {!loading && items.length === 0 && (
          <p className="py-10 text-center text-muted-foreground text-sm">
            {es ? 'Nada aquí. ¡La comunidad está limpia!' : 'Nothing here — the community is clean!'}
          </p>
        )}
        {!loading && items.length > 0 && (
          <ul className="divide-y divide-border">
            {items.map((i) => (
              <li key={i.id} className="p-3 flex items-start gap-3" data-testid={`content-mod-row-${i.id}`}>
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px] capitalize">{i.kind}</Badge>
                    <Badge variant="outline" className="text-[10px] bg-red-500/15 text-red-500 border-red-500/30">
                      <ShieldAlert className="w-2.5 h-2.5 mr-0.5 inline" />{i.category}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground">{(i.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                    {i.resolved && (
                      <Badge variant="outline" className="text-[10px] bg-emerald-500/15 text-emerald-500 border-emerald-500/30">
                        {es ? 'Revisado' : 'Reviewed'}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm break-words">“{i.text}”</p>
                  <p className="text-[10px] text-muted-foreground">{es ? 'Usuario' : 'User'}: {i.user_id}</p>
                </div>
                {!i.resolved && (
                  <Button
                    variant="outline" size="sm" className="h-7 text-[11px] shrink-0"
                    onClick={() => resolve(i.id)} disabled={actionId === i.id}
                    data-testid={`content-mod-resolve-${i.id}`}
                  >
                    {actionId === i.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                    {es ? 'Revisado' : 'Mark reviewed'}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default ContentModerationPanel;
